import { useRef, useCallback } from 'react';
import { Network } from 'vis-network';
import type { GraphData, GraphNode } from '@packages/shared';

// 调试Network导入
console.log('useNetworkManager - Network import:', typeof Network, Network);

// console.log('useNetworkManager - vis-network import:', { Network });

interface NetworkOptions {
  physics?: boolean;
  layout?: {
    randomSeed?: number;
    improvedLayout?: boolean;
  };
  nodes?: {
    shape?: string;
    size?: number;
    font?: {
      size?: number;
      color?: string;
    };
    borderWidth?: number;
    shadow?: boolean;
  };
  edges?: {
    width?: number;
    color?: { color?: string };
    arrows?: {
      to?: { enabled?: boolean; scaleFactor?: number };
    };
    smooth?: {
      enabled?: boolean;
      type?: string;
      roundness?: number;
    };
    shadow?: boolean;
  };
  interaction?: {
    hover?: boolean;
    selectConnectedEdges?: boolean;
    tooltipDelay?: number;
    hideEdgesOnDrag?: boolean;
    hideEdgesOnZoom?: boolean;
  };
}

interface UseNetworkManagerOptions {
  onNodeClick?: (node: GraphNode | null) => void;
  onNodeHover?: () => void;
  onNodeBlur?: () => void;
}

export function useNetworkManager(options?: UseNetworkManagerOptions) {
  const networkRef = useRef<Network | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentDataRef = useRef<GraphData>({ nodes: [], edges: [] });

  // 获取网络配置选项 - 完全复制Vue demo的配置
  const getNetworkOptions = useCallback((isHighPerformance: boolean = false) => {
    if (isHighPerformance) {
      // 高性能模式配置
      return {
        physics: {
          enabled: true,
          stabilization: { 
            enabled: true,
            iterations: 50,
            updateInterval: 25
          },
          barnesHut: {
            gravitationalConstant: -8000,
            springConstant: 0.05,
            springLength: 50,
            damping: 0.9
          },
          maxVelocity: 30,
          minVelocity: 0.75,
          timestep: 0.5
        },
        layout: {
          improvedLayout: true,
          randomSeed: 2
        },
        nodes: {
          shape: 'dot',
          size: 12,
          font: {
            size: 11,
            color: '#333333'
          },
          borderWidth: 2,
          shadow: false
        },
        edges: {
          width: 1,
          color: { color: '#666666' },
          arrows: {
            to: { enabled: true, scaleFactor: 0.3 },
            from: { enabled: false, scaleFactor: 0.3 } // 默认不显示from箭头，由具体边类型决定
          },
          smooth: {
            enabled: true,
            type: 'dynamic',
            roundness: 0.5
          },
          shadow: false,
          font: {
            size: 8,
            color: '#666666',
            strokeWidth: 0,
            align: 'horizontal'
          }
        },
        interaction: {
          hover: false,
          selectConnectedEdges: false,
          tooltipDelay: 300,
          hideEdgesOnDrag: true,
          hideEdgesOnZoom: true
        }
      };
    } else {
      // 普通模式配置
      return {
        physics: {
          enabled: true,
          stabilization: { iterations: 100 }
        },
        layout: {
          improvedLayout: true,
          randomSeed: 2
        },
        nodes: {
          shape: 'dot',
          size: 12,
          font: {
            size: 11,
            color: '#333333'
          },
          borderWidth: 2,
          shadow: true
        },
        edges: {
          width: 1,
          color: { color: '#666666' },
          arrows: {
            to: { enabled: true, scaleFactor: 0.3 },
            from: { enabled: false, scaleFactor: 0.3 } // 默认不显示from箭头，由具体边类型决定
          },
          smooth: {
            enabled: true,
            type: 'dynamic',
            roundness: 0.5
          },
          shadow: true,
          font: {
            size: 8,
            color: '#666666',
            strokeWidth: 0,
            align: 'horizontal'
          }
        },
        interaction: {
          hover: true,
          selectConnectedEdges: false,
          tooltipDelay: 200,
          hideEdgesOnDrag: false,
          hideEdgesOnZoom: false
        }
      };
    }
  }, []);

  // 初始化网络
  const initNetwork = useCallback((data: GraphData, isHighPerformance: boolean = false) => {
    if (!containerRef.current) {
      console.log('Container not ready for network initialization');
      return;
    }

    console.log('Initializing network with container:', containerRef.current);

    try {
      // 如果已有网络实例且数据结构相同，使用updateData而不是重新初始化
      if (networkRef.current) {
        console.log('Network already exists, updating data instead of recreating');
        networkRef.current.setData(data);
        const networkOptions = getNetworkOptions(isHighPerformance);
        networkRef.current.setOptions(networkOptions);
        currentDataRef.current = data;
        return;
      }

      const networkOptions = getNetworkOptions(isHighPerformance);

      try {
        console.log('Creating Network instance with:', {
          container: containerRef.current,
          data: data,
          options: networkOptions,
          Network: typeof Network
        });
        // 直接使用原始数据，就像Vue demo一样
        networkRef.current = new Network(containerRef.current, data, networkOptions);
        console.log('Network initialized successfully');
        currentDataRef.current = data;
      } catch (networkError) {
        console.error('Failed to create Network instance:', networkError);
        throw networkError;
      }

      // 事件监听
      networkRef.current.on('click', (params) => {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          const node = data.nodes.find(n => n.id === nodeId);
          options?.onNodeClick?.(node || null);
        } else {
          options?.onNodeClick?.(null);
        }
      });

      networkRef.current.on('hoverNode', () => {
        if (containerRef.current) {
          containerRef.current.style.cursor = 'pointer';
        }
        options?.onNodeHover?.();
      });

      networkRef.current.on('blurNode', () => {
        if (containerRef.current) {
          containerRef.current.style.cursor = 'default';
        }
        options?.onNodeBlur?.();
      });

      // 延迟适配视图，等待网络稳定
      setTimeout(() => {
        if (networkRef.current && typeof networkRef.current.fit === 'function') {
          networkRef.current.fit();
        }
      }, 1000);
    } catch (error) {
      console.error('Network initialization failed:', error);
    }
  }, [getNetworkOptions, options]);

  // 更新网络数据
  const updateData = useCallback((data: GraphData) => {
    if (networkRef.current) {
      console.log('Updating network data:', data);
      // 直接使用原始数据
      networkRef.current.setData(data);
      currentDataRef.current = data;
    }
  }, []);

  // 更新网络配置
  const updateOptions = useCallback((isHighPerformance: boolean = false) => {
    if (networkRef.current) {
      const networkOptions = getNetworkOptions(isHighPerformance);
      networkRef.current.setOptions(networkOptions);
    }
  }, [getNetworkOptions]);

  // 聚焦到指定节点
  const focusOnNode = useCallback((nodeId: string, isHighPerformance: boolean = false) => {
    if (!networkRef.current) {
      console.log('Network not available for focus');
      return;
    }

    console.log('Focusing on node with options:', { nodeId, isHighPerformance });

    const focusOptions = isHighPerformance
      ? {
          scale: 1.5,
          animation: false
        }
      : {
          scale: 1.5,
          animation: {
            duration: 1000,
            easingFunction: 'easeInOutQuad' as any
          }
        };

    networkRef.current.focus(nodeId, focusOptions);
    networkRef.current.selectNodes([nodeId]);
    console.log('Node focused successfully');
  }, []);

  // 销毁网络
  const destroyNetwork = useCallback(() => {
    if (networkRef.current) {
      networkRef.current.destroy();
      networkRef.current = null;
    }
  }, []);

  return {
    containerRef,
    networkRef,
    currentDataRef,
    initNetwork,
    updateData,
    updateOptions,
    focusOnNode,
    destroyNetwork
  };
}